import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { usePostHog } from "@posthog/react";
import instance from "../api/api";
import { Loader } from "./Loader";
import { useTenant } from "../context/Tenant/TenantContext";
import { getBrandTheme } from "../utils/tenantTheme";

const THEMES = {
  blueprint: { lp: "#1c3562", pri: "#1d4ed8" },
  obsidian:  { lp: "#0c1220", pri: "#3b82f6" },
  daylight:  { lp: "#1e40af", pri: "#0284c7" },
};

const INPUT_STYLE = {
  width: "100%",
  padding: "11px 14px",
  border: "1.5px solid #e5e7eb",
  borderRadius: 9,
  fontSize: 14,
  color: "#0f172a",
  background: "#fafafa",
  outline: "none",
  fontFamily: "inherit",
  boxSizing: "border-box",
};

export default function ResetPassword() {
  const posthog  = usePostHog();
  const navigate = useNavigate();
  const location = useLocation();
  const token    = new URLSearchParams(location.search).get("token");
  const { tenant, loading: tenantLoading } = useTenant();

  const theme = localStorage.getItem("riskTheme") || "daylight";
  const t = getBrandTheme(tenant?.branding, THEMES[theme] || THEMES.daylight);
  const companyName = tenant?.companyName || "RSM Botswana";

  const [formValue,    setFormValue]    = useState({ newPassword: "", confirmPassword: "" });
  const [formErrors,   setFormErrors]   = useState({ newPassword: "", confirmPassword: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const [pageError,    setPageError]    = useState("");

  if (tenantLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <Loader size="xl" />
      </div>
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValue(prev => ({ ...prev, [name]: value }));
  };

  const validate = (value) => {
    const errors = { newPassword: "", confirmPassword: "" };
    const lc = /[a-z]/, uc = /[A-Z]/, sp = /[!@#$%^&*(),.?":{}|<>]/, num = /[0-9]/;
    if (!value.newPassword) {
      errors.newPassword = "Password is required";
    } else if (value.newPassword.length < 8) {
      errors.newPassword = "Password must be at least 8 characters long";
    } else if (!lc.test(value.newPassword)) {
      errors.newPassword = "Password must contain at least one lowercase letter";
    } else if (!uc.test(value.newPassword)) {
      errors.newPassword = "Password must contain at least one uppercase letter";
    } else if (!sp.test(value.newPassword)) {
      errors.newPassword = "Password must contain at least one special character";
    } else if (!num.test(value.newPassword)) {
      errors.newPassword = "Password must contain at least one number";
    }
    if (!value.confirmPassword) {
      errors.confirmPassword = "Confirm Password is required";
    } else if (value.newPassword !== value.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validate(formValue);
    setFormErrors(errors);
    if (Object.values(errors).some(Boolean)) return;
    setIsSubmitting(true);
    setPageError("");
    try {
      const response = await instance.post("/api/auth/reset-password", {
        token,
        newPassword: formValue.newPassword,
        confirmPassword: formValue.confirmPassword,
      });
      posthog.capture("password_reset_completed");
      setResponseMessage(response.data.message || "Password reset successfully! Redirecting…");
      setTimeout(() => navigate("/"), 3000);
    } catch (error) {
      posthog.capture("password_reset_failed", { error: error.response?.data?.message });
      setPageError(error.response?.data?.message ?? "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>

      {/* ── Left brand panel ── */}
      <div style={{
        width: "42%", flexShrink: 0,
        background: t.lp,
        display: "flex", flexDirection: "column", justifyContent: "space-between",
        padding: "48px 44px", position: "relative", overflow: "hidden",
      }}>
        <div style={{ position: "absolute", top: -100, right: -100, width: 400, height: 400, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.06)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", top: -50, right: -50, width: 250, height: 250, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.09)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: 80, left: -80, width: 320, height: 320, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.05)", pointerEvents: "none" }} />

        <div style={{ position: "relative", zIndex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 52 }}>
            <div style={{
              width: 38, height: 38, borderRadius: 10,
              background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.25)",
              display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden",
            }}>
              <img src={tenant?.branding?.logoUrl || "/rsmLogo.png"} alt="logo" style={{ width: 30, height: 30, objectFit: "contain" }} />
            </div>
            <span style={{ color: "#fff", fontSize: 17, fontWeight: 700, letterSpacing: "-0.3px" }}>{companyName}</span>
          </div>
          <h1 style={{ color: "#fff", fontSize: 30, fontWeight: 800, lineHeight: 1.2, letterSpacing: "-0.5px", margin: "0 0 14px 0", maxWidth: 280 }}>
            Enterprise Risk Management Platform
          </h1>
          <p style={{ color: "rgba(255,255,255,0.55)", fontSize: 14, lineHeight: 1.7, maxWidth: 280, margin: 0 }}>
            Identify, assess, and manage organizational risks with confidence across every department.
          </p>
        </div>

        <p style={{ position: "relative", zIndex: 1, color: "rgba(255,255,255,0.35)", fontSize: 12, margin: 0 }}>
          Secure • Role-based • Multi-tenant
        </p>
      </div>

      {/* ── Right form panel ── */}
      <div style={{
        flex: 1, background: "#fff",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        padding: 48,
      }}>
        <div style={{ width: "100%", maxWidth: 360 }}>

          {/* Back link */}
          <a
            href="/"
            style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "#94a3b8", textDecoration: "none", marginBottom: 32, fontWeight: 500 }}
          >
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to login
          </a>

          <h2 style={{ fontSize: 26, fontWeight: 800, color: "#0f172a", letterSpacing: "-0.5px", margin: "0 0 6px 0" }}>
            Reset your password
          </h2>
          <p style={{ color: "#94a3b8", fontSize: 14, margin: "0 0 28px 0", lineHeight: 1.6 }}>
            Enter your new password below.
          </p>

          {responseMessage && (
            <div style={{ padding: "12px 16px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 9, marginBottom: 20, fontSize: 13, color: "#16a34a", fontWeight: 500 }}>
              {responseMessage}
            </div>
          )}
          {pageError && (
            <div style={{ padding: "12px 16px", background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 9, marginBottom: 20, fontSize: 13, color: "#dc2626", fontWeight: 500 }}>
              {pageError}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }} htmlFor="newPassword">
                New Password
              </label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                placeholder="••••••••"
                style={INPUT_STYLE}
                value={formValue.newPassword}
                onChange={handleChange}
                required
              />
              {formErrors.newPassword && <p style={{ fontSize: 12, color: "#dc2626", marginTop: 4 }}>{formErrors.newPassword}</p>}
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }} htmlFor="confirmPassword">
                Confirm Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                placeholder="••••••••"
                style={INPUT_STYLE}
                value={formValue.confirmPassword}
                onChange={handleChange}
                required
              />
              {formErrors.confirmPassword && <p style={{ fontSize: 12, color: "#dc2626", marginTop: 4 }}>{formErrors.confirmPassword}</p>}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                width: "100%", padding: 13, background: t.pri, color: "#fff",
                border: "none", borderRadius: 9, fontSize: 15, fontWeight: 700,
                cursor: isSubmitting ? "not-allowed" : "pointer",
                fontFamily: "inherit", opacity: isSubmitting ? 0.8 : 1, transition: "opacity 0.15s",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              {isSubmitting ? <Loader size="sm" /> : "Reset Password"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
